import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, MapPin, Users, Building2 } from 'lucide-react';
import { type Venue } from '@peles/shared';
import { useAuth } from '../context/AuthContext';
import { useVenues } from '../hooks/useVenues';
import { EntityList, StatusBadge } from '../components/common';
import type { EntityListColumn } from '../components/common';
import { EntityModal } from '../components/common/EntityModal';
import { CreateVenuePanel } from '../components/venues';

const PAGE_SIZE = 25;

export function VenuesPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [search, setSearch] = useState('');
  const [activeFilter, setActiveFilter] = useState('');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);

  const apiFilters: { isActive?: boolean } = {};
  if (activeFilter) apiFilters.isActive = activeFilter === 'true';

  const { data: allVenues = [], isLoading } = useVenues(
    Object.keys(apiFilters).length > 0 ? apiFilters : undefined,
  );

  const filtered = useMemo(() => {
    if (!search) return allVenues;
    const term = search.toLowerCase();
    return allVenues.filter(
      (v) =>
        v.name.toLowerCase().includes(term) ||
        v.city?.toLowerCase().includes(term) ||
        v.address?.toLowerCase().includes(term),
    );
  }, [allVenues, search]);

  const total = filtered.length;
  const items = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const columns: EntityListColumn<Venue>[] = useMemo(
    () => [
      {
        key: 'name',
        header: 'Venue',
        render: (venue) => (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Building2 size={16} className="text-primary-600" />
            </div>
            <div>
              <p className="text-gray-900 font-semibold">{venue.name}</p>
              {venue.contactName && <p className="text-xs text-gray-500">{venue.contactName}</p>}
            </div>
          </div>
        ),
      },
      {
        key: 'location',
        header: 'Location',
        render: (venue) =>
          venue.city ? (
            <span className="text-sm text-gray-600 flex items-center gap-1">
              <MapPin size={12} className="text-gray-400" />
              {[venue.city, venue.state].filter(Boolean).join(', ')}
            </span>
          ) : (
            <span className="text-gray-400 text-sm">—</span>
          ),
        hideOnMobile: true,
      },
      {
        key: 'capacity',
        header: 'Capacity',
        render: (venue) =>
          venue.capacity ? (
            <span className="text-sm text-gray-600 flex items-center gap-1">
              <Users size={12} className="text-gray-400" />
              {venue.capacity}
            </span>
          ) : (
            <span className="text-gray-400 text-sm">—</span>
          ),
        width: 'w-24',
        hideOnMobile: true,
      },
      {
        key: 'status',
        header: 'Status',
        render: (venue) => (
          <StatusBadge
            label={venue.isActive ? 'Active' : 'Inactive'}
            color={venue.isActive ? 'green' : 'gray'}
          />
        ),
        width: 'w-24',
      },
    ],
    [],
  );

  if (!currentUser) return null;

  return (
    <div>
      <EntityList
        items={items}
        columns={columns}
        getKey={(venue) => venue.id}
        isLoading={isLoading}
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
        searchValue={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        searchPlaceholder="Search venues..."
        onRowClick={(venue) => navigate(`/venues/${venue.id}`)}
        onPreview={(venue) => navigate(`/venues/${venue.id}`)}
        title="Venues"
        titleActions={
          <button
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-2 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Plus size={16} />
            New Venue
          </button>
        }
        filterBar={
          <div className="flex items-center gap-2">
            <select
              value={activeFilter}
              onChange={(e) => {
                setActiveFilter(e.target.value);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        }
        emptyMessage="No venues found"
      />

      <EntityModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Venue"
        size="md"
      >
        <CreateVenuePanel
          onClose={() => setShowCreate(false)}
          onCreated={() => setShowCreate(false)}
        />
      </EntityModal>
    </div>
  );
}
